// /admin/conversations/:id — operator console: diagnose one conversation
//. The thread on the left; clicking a bot turn opens its full
// pipeline trace (classification → RAG → generation → handoff) in the inspector.
// Data via the getConversationDebug() seam (wired to GET
// /admin/conversations/:id/debug). "Copiar diagnóstico" copies the turn trace as
// JSON; "Ver en inbox" deep-links the tenant inbox. Export/report have no backend
// yet and show coming-soon toasts.

import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  type ConversationDebug,
  getConversationDebug,
  type Inspection,
  returnConversationToBot,
  type ThreadItem,
} from './conversation-debug-data.js'
import { useSession } from '../../auth/session.js'
import {
  Button,
  Chip,
  EmptyStateCard,
  PageHeader,
  Spinner,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { comingSoon } from '../../lib/coming-soon.js'

function ConfBar({ score }: { score: number }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-1 w-12 overflow-hidden rounded-pill bg-field align-middle">
        <span
          className="block h-full rounded-pill bg-brand-blue"
          style={{ width: `${score * 100}%` }}
        />
      </span>
      <span className="font-mono text-[10.5px] tabular-nums text-text-2">{score.toFixed(2)}</span>
    </span>
  )
}

function ISect({ title, children }: { title: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <section className="border-b border-line last:border-b-0">
      <h3 className="px-4 py-2.5 text-[11px] font-bold text-text">{title}</h3>
      <div className="px-4 pb-3.5">{children}</div>
    </section>
  )
}

function KvGrid({ children }: { children: ReactNode }): JSX.Element {
  return <dl className="flex flex-col gap-1.5">{children}</dl>
}
function Kv({ k, children }: { k: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 text-[11.5px]">
      <dt className="flex-none text-text-3">{k}</dt>
      <dd className="min-w-0 text-right text-text">{children}</dd>
    </div>
  )
}

function Inspector({ insp }: { insp: Inspection }): JSX.Element {
  async function copyDiagnostic(): Promise<void> {
    try {
      await navigator.clipboard.writeText(JSON.stringify(insp, null, 2))
      toast.ok('Diagnóstico copiado', {
        description: 'El trazo del turno está en el portapapeles.',
      })
    } catch {
      toast.error('No se pudo copiar', { description: 'El navegador bloqueó el portapapeles.' })
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-none border-b border-line bg-surface-2 px-4 py-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.06em] text-text-3">
          Inspector · respuesta del agente
        </div>
        <div className="mt-0.5 text-[13px] font-bold text-text">{insp.turnTitle}</div>
        <div className="mt-1 font-mono text-[10.5px] text-text-3">{insp.msgId}</div>
        <div className="text-[10.5px] text-text-3">{insp.time}</div>
      </div>

      <div
        tabIndex={0}
        aria-label="Diagnóstico del turno"
        className="min-h-0 flex-1 overflow-y-auto focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-brand-blue"
      >
        <ISect title="Mensaje del usuario que disparó este turno">
          <p className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-[11.5px] italic leading-relaxed text-text-2">
            “{insp.userMessage}”
          </p>
        </ISect>

        {/* Model name is hardcoded: the trace does not carry which model ran the
            classify/generate step yet. Surface insp's model here once it does. */}
        <ISect title="Clasificación · Haiku 4.5">
          <KvGrid>
            {insp.classification.map((c) => (
              <Kv key={c.k} k={c.k}>
                <span className="inline-flex items-center gap-2">
                  <span className={c.tone === 'ok' ? 'text-ok' : 'text-text'}>{c.label}</span>
                  {c.score !== undefined && <ConfBar score={c.score} />}
                </span>
              </Kv>
            ))}
          </KvGrid>
        </ISect>

        {/* Per-turn RAG retrieval isn't logged in Phase 1, so the trace reports
            "0 de 0". Hide the section entirely rather than show an empty pipeline
            stage that looks broken. */}
        {insp.rag.total > 0 ? (
          <ISect title={`Recuperación (RAG) · ${insp.rag.used} de ${insp.rag.total} fragmentos`}>
            <div className="flex flex-col gap-2">
              {insp.rag.chunks.map((ch) => (
                <div
                  key={ch.source}
                  className="rounded-lg border border-line bg-surface-2 px-3 py-2"
                >
                  <div className="flex items-center gap-2 text-[10.5px]">
                    <span className="min-w-0 flex-1 truncate font-semibold text-text">
                      {ch.source}
                    </span>
                    <span className="text-text-3">{ch.page}</span>
                    <span className="font-mono font-bold text-link">{ch.score.toFixed(2)}</span>
                  </div>
                  <p className="mt-1 text-[11px] italic leading-snug text-text-3">“{ch.snippet}”</p>
                </div>
              ))}
            </div>
          </ISect>
        ) : null}

        {/* Model name is hardcoded (see note above). */}
        <ISect title="Generación · Claude Sonnet 4.6">
          <KvGrid>
            {insp.generation.map((g) => (
              <Kv key={g.k} k={g.k}>
                <span className={`font-mono ${g.tone === 'ok' ? 'text-ok' : 'text-text'}`}>
                  {g.v}
                </span>
              </Kv>
            ))}
          </KvGrid>
        </ISect>

        <ISect title="Evaluación de handoff">
          <KvGrid>
            <Kv k="Reglas evaluadas">{insp.handoff.rulesEvaluated}</Kv>
            <Kv k="Disparadas">
              <span className="text-ok">{insp.handoff.triggered}</span>
            </Kv>
          </KvGrid>
          <div className="mt-2.5 flex items-center gap-1.5 rounded-lg bg-ok-bg px-3 py-2 text-[12px] font-semibold text-ok [&_svg]:size-[14px]">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            {insp.handoff.result}
          </div>
        </ISect>
      </div>

      <div className="flex flex-none gap-2 border-t border-line px-4 py-3">
        <Button variant="secondary" size="sm" onClick={() => void copyDiagnostic()}>
          Copiar diagnóstico
        </Button>
        <Button variant="secondary" size="sm" onClick={() => comingSoon('Reportar el turno')}>
          Reportar
        </Button>
      </div>
    </div>
  )
}

function ThreadMsg({
  item,
  selected,
  onSelect,
}: {
  item: Extract<ThreadItem, { type: 'msg' }>
  selected: boolean
  onSelect: () => void
}): JSX.Element {
  const isUser = item.sender === 'user'
  const inspectable = Boolean(item.inspectionId)
  // sender:'agent' is the HUMAN teammate (the "Asesor"), not the AI "Agente" —
  // the AI turn is sender:'bot'. The visible label comes from item.author.
  const bubble = (
    <div
      className={`max-w-[85%] rounded-xl px-3 py-2 text-[12.5px] leading-relaxed ${
        item.sender === 'user'
          ? 'bg-field text-text'
          : item.sender === 'agent'
            ? 'bg-[color-mix(in_srgb,var(--brand-blue)_8%,transparent)] text-text'
            : 'bg-surface text-text'
      } ${item.sender === 'bot' ? 'border border-line' : ''} ${
        selected ? 'outline outline-2 outline-offset-2 outline-brand-blue' : ''
      }`}
    >
      {item.text}
    </div>
  )
  return (
    <div className={`flex flex-col gap-1 ${isUser ? 'items-start' : 'items-end'}`}>
      <div className="flex items-center gap-1.5 text-[10.5px] text-text-3">
        <b className="font-semibold text-text-2">{item.author}</b>
        {item.sender === 'agent' ? (
          <span className="rounded-pill bg-field px-1.5 text-[9px] font-bold text-text-2">
            Asesor
          </span>
        ) : null}
        · {item.time}
        {selected && (
          <span className="rounded-pill bg-brand-blue px-1.5 text-[9px] font-bold text-white">
            Inspeccionando
          </span>
        )}
      </div>
      {inspectable ? (
        <button type="button" onClick={onSelect} className="flex max-w-full justify-end text-left">
          {bubble}
        </button>
      ) : (
        bubble
      )}
    </div>
  )
}

/**
 * Download the whole trace as JSON — the point of a diagnosis is being able to
 * hand it to someone. Client-side, so what you send is exactly what you saw.
 */
function exportDebugJson(debug: unknown): void {
  const blob = new Blob([JSON.stringify(debug, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `vysto-diagnostico-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`
  a.click()
  URL.revokeObjectURL(url)
}

export function ConversationDebugPage(): JSX.Element {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'conversation-debug', id],
    queryFn: () => getConversationDebug(id),
  })
  const [selected, setSelected] = useState<string>('')

  useEffect(() => {
    if (data && !selected) {
      const first = data.thread.find((t) => t.type === 'msg' && t.inspectionId)
      if (first && first.type === 'msg' && first.inspectionId) setSelected(first.inspectionId)
    }
  }, [data, selected])

  if (isLoading) {
    return (
      <div className="py-10">
        <Spinner label="Cargando diagnóstico…" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="mx-auto w-full max-w-2xl">
        <PageHeader title="Conversación no encontrada" />
        <div className="mt-6">
          <EmptyStateCard
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path
                  d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
            title="No encontramos esta conversación"
            body="Puede que el enlace sea incorrecto."
            actions={<Button onClick={() => void navigate('/admin/tenants')}>Volver</Button>}
          />
        </div>
      </div>
    )
  }

  const insp = data.inspections[selected]

  return (
    <div data-testid="conversation-debug" className="flex h-full flex-col">
      <ThreadHeader data={data} conversationId={id} />

      <div className="mt-4 grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[1fr,360px]">
        <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-line bg-surface">
          <div className="flex flex-none items-center justify-between gap-2 border-b border-line px-4 py-2.5">
            <span className="text-[12.5px] font-bold text-text">Hilo de la conversación</span>
            <span className="text-[10.5px] text-text-3">
              Toca un mensaje del agente para inspeccionar
            </span>
          </div>
          <div
            tabIndex={0}
            aria-label="Hilo de la conversación"
            className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4 focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-brand-blue"
          >
            {data.thread.map((item) => {
              if (item.type === 'day')
                return (
                  <div
                    key={item.id}
                    className="my-1 text-center text-[10.5px] font-semibold text-text-3"
                  >
                    {item.label}
                  </div>
                )
              if (item.type === 'event')
                return (
                  <div
                    key={item.id}
                    className="mx-auto flex items-center gap-1.5 rounded-pill bg-sens-bg px-3 py-1 text-[10.5px] font-semibold text-sens"
                  >
                    {item.text}
                  </div>
                )
              if (item.type === 'note')
                return (
                  <div
                    key={item.id}
                    className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-[11px] leading-snug text-text-2"
                  >
                    <div className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-text-3">
                      Nota interna · generada automáticamente
                    </div>
                    {item.text}
                  </div>
                )
              return (
                <ThreadMsg
                  key={item.id}
                  item={item}
                  selected={Boolean(item.inspectionId) && item.inspectionId === selected}
                  onSelect={() => item.inspectionId && setSelected(item.inspectionId)}
                />
              )
            })}
          </div>
        </section>

        <aside className="min-h-0 overflow-hidden rounded-xl border border-line bg-surface">
          {insp ? (
            <Inspector insp={insp} />
          ) : (
            <div className="grid h-full place-items-center p-6 text-center text-[12px] text-text-3">
              Toca una respuesta del agente para ver su diagnóstico.
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}

function ThreadHeader({
  data,
  conversationId,
}: {
  data: ConversationDebug
  conversationId: string
}): JSX.Element {
  const navigate = useNavigate()
  const session = useSession()
  const [returning, setReturning] = useState(false)
  const [returnMsg, setReturnMsg] = useState('')

  async function handleReturnToBot(): Promise<void> {
    setReturning(true)
    setReturnMsg('')
    try {
      await returnConversationToBot(conversationId)
      setReturnMsg('Conversación devuelta al agente.')
    } catch (err) {
      // Surface the real API reason (e.g. "la conversación no está en handoff")
      // instead of guessing a single cause.
      setReturnMsg(apiErrorMessage(err, 'No se pudo devolver la conversación al agente.'))
    } finally {
      setReturning(false)
    }
  }

  return (
    <div className="flex-none">
      <PageHeader
        breadcrumb={[{ label: 'Clientes', href: '/admin/tenants' }, { label: 'Conversaciones' }]}
        title={data.contact.name}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void handleReturnToBot()}
              loading={returning}
            >
              Devolver al agente
            </Button>
            {/* /inbox is the TENANT's Bandeja — staff have no membership
                there, so for them the button was a door into a 403. */}
            {session?.context !== 'admin' ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void navigate(`/bandeja?c=${encodeURIComponent(conversationId)}`)}
              >
                Ver en inbox
              </Button>
            ) : null}
            <Button variant="secondary" size="sm" onClick={() => exportDebugJson(data)}>
              Exportar
            </Button>
          </div>
        }
      />
      {returnMsg ? <p className="mt-1 text-[12px] text-text-2">{returnMsg}</p> : null}
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
        {data.pills.map((p) => (
          <Chip key={p.label} tone={p.tone} dot>
            {p.label}
          </Chip>
        ))}
        <span className="flex flex-wrap items-center gap-x-2 text-[11px] text-text-3">
          {data.meta.map((m) => (
            <span
              key={m}
              className="after:ml-2 after:text-text-3/40 after:content-['·'] last:after:hidden"
            >
              {m}
            </span>
          ))}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
        {data.stats.map((s) => (
          <div key={s.label} className="rounded-lg border border-line bg-surface px-3 py-2.5">
            <div className="text-[9.5px] font-bold uppercase tracking-[0.04em] text-text-3">
              {s.label}
            </div>
            <div
              className={`mt-1 text-[14px] font-extrabold leading-tight text-text ${
                s.tone === 'warn' ? 'text-warn' : ''
              } ${s.tone === 'mono' ? 'font-mono text-[12.5px]' : ''}`}
            >
              {s.value}
            </div>
            <div className="mt-0.5 text-[10px] text-text-3">{s.sub}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
